import { useIdentityContext } from '@flik/shared';
import { useTheme } from '@flik/shared/components';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavBar from '../components/NavBar';

const SignInPage: React.FC = () => {
  const { provideTailwind } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    signInState,
    signIn,
    verifyCode,
    resendCode,
    resetSignIn,
    setSignInState,
    isLoggedIn,
  } = useIdentityContext();

  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  // Redirect to home or referrer if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      const referrer = searchParams.get('referrer');
      if (referrer) {
        navigate(referrer);
      } else {
        navigate('/');
      }
    }
  }, [isLoggedIn, navigate, searchParams]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      // Clear any existing success messages when starting fresh
      setSignInState((prev: any) => ({
        ...prev,
        successMessage: null,
      }));
      await signIn(email.trim());
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.trim()) {
      await verifyCode(verificationCode.trim());
    }
  };

  const handleBackToEmail = () => {
    resetSignIn();
    setEmail('');
    setVerificationCode('');
  };

  const handleResendCode = async () => {
    setVerificationCode(''); // Clear the local state immediately
    await resendCode();
  };

  // Handle verification expiration - take user back to email screen
  const handleVerificationExpired = () => {
    resetSignIn();
    setEmail('');
    setVerificationCode('');
    // Set a temporary message to inform the user
    setSignInState((prev: any) => ({
      ...prev,
      successMessage:
        'Verification code expired. Please enter your email again.',
    }));
  };

  // Monitor for verification expiration error
  useEffect(() => {
    if (signInState.error && signInState.error.includes('Resource not found')) {
      handleVerificationExpired();
    }
  }, [signInState.error]);

  // Auto-progress to verification screen when there's a session OTP
  useEffect(() => {
    if (signInState.error && signInState.error.includes('Session OTP exists')) {
      setSignInState((prev: any) => ({
        ...prev,
        step: 'verification',
        error: null, // Clear the error since we're handling it by progressing
      }));
    }
  }, [signInState.error, setSignInState]);

  // If user is logged in, don't show anything (will redirect)
  if (isLoggedIn) {
    return null;
  }

  return (
    <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
      <NavBar />

      <div className="max-w-sm mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h1
            className={`text-4xl font-bold mb-3 ${provideTailwind(
              'text-primary',
            )}`}
          >
            Welcome back
          </h1>
          <p className={`text-base ${provideTailwind('text-secondary')}`}>
            {signInState.step === 'email'
              ? 'Enter your email to get started'
              : 'Check your email for the verification code'}
          </p>
        </div>

        <div className="space-y-6">
          {signInState.error &&
            !signInState.error.includes('Session OTP exists') &&
            !signInState.error.includes(
              'Session verification code does not match expected',
            ) &&
            !signInState.error.includes('Resource not found') && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-200">
                {signInState.error}
              </div>
            )}

          {signInState.successMessage && (
            <div
              className={`px-4 py-3 rounded-lg text-sm ${
                signInState.successMessage.includes('expired')
                  ? 'bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200'
                  : 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
              }`}
            >
              {signInState.successMessage}
            </div>
          )}

          {signInState.step === 'email' ? (
            <form className="space-y-4" onSubmit={handleEmailSubmit}>
              <div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full px-4 py-4 text-lg border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 transition-colors"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={signInState.isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={signInState.isLoading || !email.trim()}
                className="w-full py-4 px-4 bg-pink-600 text-white text-lg font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {signInState.isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : null}
                Continue
              </button>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={handleVerificationSubmit}>
              <div>
                <input
                  id="verification-code"
                  name="verification-code"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  maxLength={8}
                  className="w-full px-4 py-4 text-2xl text-center tracking-widest border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 transition-colors"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => {
                    const newValue = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, '');
                    setVerificationCode(newValue);
                    // Clear success message when user starts typing
                    if (newValue.length > 0 && signInState.successMessage) {
                      setSignInState((prev: any) => ({
                        ...prev,
                        successMessage: null,
                      }));
                    }
                  }}
                  disabled={signInState.isLoading}
                />
              </div>

              {/* Verification code error message */}
              {signInState.error &&
                signInState.error.includes(
                  'Session verification code does not match expected',
                ) && (
                  <div className="text-center">
                    <span className="text-sm text-red-600 dark:text-red-400">
                      Incorrect code. Please try again.
                    </span>
                  </div>
                )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  disabled={signInState.isLoading}
                  className="flex-1 py-4 px-4 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={
                    signInState.isLoading || verificationCode.length === 0
                  }
                  className="flex-1 py-4 px-4 bg-pink-600 text-white text-lg font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {signInState.isLoading ? (
                    <svg
                      className="animate-spin h-5 w-5 text-white mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : null}
                  Verify
                </button>
              </div>

              {/* Resend code link */}
              <div className="text-center pt-2">
                <span
                  className={`text-sm ${provideTailwind('text-tertiary')} mr-1`}
                >
                  Didn't receive the code?
                </span>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={signInState.isLoading}
                  className="text-sm text-pink-600 hover:text-pink-700 font-medium transition-colors focus:outline-none dark:text-pink-400 dark:hover:text-pink-300"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
